/**
 * 主应用组件
 */
import React from 'react';
import axios from 'axios';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { useAppState } from './hooks/useAppState';
import { useAuth } from './hooks/useAuth';
import StepBar from './components/StepBar';
import DocumentAnalysis from './pages/DocumentAnalysis';
import OutlineEdit from './pages/OutlineEdit';
import ContentEdit from './pages/ContentEdit';
import Login from './pages/Login';
import { CurrentUser, authApi } from './services/api';
import { draftStorage } from './utils/draftStorage';
import VisualLogin from './visual/VisualLogin';
import VisualWorkspace from './visual/VisualWorkspace';

interface AuthenticatedAppProps {
  user: CurrentUser;
  onLogout: () => void;
}

const AuthenticatedApp: React.FC<AuthenticatedAppProps> = ({ user, onLogout }) => {
  draftStorage.setMemberId(user.id);

  const {
    state,
    updateStep,
    updateFileContent,
    updateAnalysisResults,
    updateOutline,
    nextStep,
    prevStep,
    resetState,
  } = useAppState();

  const steps = ['标书解析', '目录编辑', '正文编辑'];

  const handleReset = () => {
    if (!window.confirm('确定要清理所有缓存数据并从头开始吗？')) {
      return;
    }

    draftStorage.clearAll();
    resetState();
  };

  const handleLogout = () => {
    draftStorage.setMemberId(null);
    onLogout();
  };

  const renderCurrentPage = () => {
    switch (state.currentStep) {
      case 0:
        return (
          <DocumentAnalysis
            fileContent={state.fileContent}
            projectOverview={state.projectOverview}
            techRequirements={state.techRequirements}
            onFileUpload={updateFileContent}
            onAnalysisComplete={updateAnalysisResults}
          />
        );
      case 1:
        return (
          <OutlineEdit
            projectOverview={state.projectOverview}
            techRequirements={state.techRequirements}
            outlineData={state.outlineData}
            onOutlineGenerated={updateOutline}
          />
        );
      case 2:
        return (
          <ContentEdit
            outlineData={state.outlineData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* 主内容区域 */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* 步骤导航 */}
        <div className="sticky top-0 z-50 flex items-center gap-6 bg-white px-6 shadow-sm">
          <div className="min-w-0 flex-1">
          <StepBar steps={steps} currentStep={state.currentStep} />
          </div>
          <div className="flex shrink-0 items-center gap-3 text-sm">
            <span className="text-gray-600">{user.username}</span>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50"
            >
              退出登录
            </button>
          </div>
        </div>

        {/* 页面内容 */}
        <div id="app-main-scroll" className="flex-1 p-6 overflow-y-auto">
          {renderCurrentPage()}
        </div>

        {/* 底部导航按钮 */}
        <div className="sticky bottom-0 z-50 bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex justify-between">
            <div className="flex items-center space-x-3">
              {state.currentStep === 0 && (
                <button
                  onClick={handleReset}
                  title="清理所有缓存数据，从头开始"
                  className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  重置
                </button>
              )}

              <button
                onClick={() => updateStep(0)}
                disabled={state.currentStep === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
              >
                首页
              </button>

              <button
                onClick={prevStep}
                disabled={state.currentStep === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                上一步
              </button>
            </div>

            <button
              onClick={nextStep}
              disabled={state.currentStep === steps.length - 1}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              下一步
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 微信新用户首次登录时弹出的绑定用户名密码弹窗
 */
interface BindInfoModalProps {
  onBind: (username: string, password: string) => Promise<void>;
  onClose: () => void;
}

const BindInfoModal: React.FC<BindInfoModalProps> = ({ onBind, onClose }) => {
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('请输入用户名');
      return;
    }
    if (password.length < 6 || password.length > 16) {
      setError('密码长度需为 6-16 位');
      return;
    }
    if (/^\d+$/.test(password) || /^[a-zA-Z]+$/.test(password)) {
      setError('密码不能纯数字或纯字母');
      return;
    }
    if (password !== confirmPassword) {
      setError('两次密码不一致');
      return;
    }

    setSubmitting(true);
    try {
      await onBind(username.trim(), password);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || '绑定失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md mx-4 rounded-xl bg-white p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 右上角关闭按钮 */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="关闭"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">欢迎注册</h2>
          <p className="mt-1 text-sm text-gray-500">
            微信登录成功，请设置用户名和密码，方便下次直接登录
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              用户名
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="请输入用户名"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              密码
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="6-16位，不能纯数字或纯字母"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              确认密码
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="请再次输入密码"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? '绑定中...' : '完成注册'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/** 第三方扫码平台地址（与 VisualLogin 一致） */
const ZBAPI_POLL_URL = 'https://zbapi.ksmso.cn/index/index/ticket';
const WECHAT_POLL_INTERVAL_MS = 4000;
const WECHAT_POLL_MAX_ATTEMPTS = 12;

interface BindWechatModalProps {
  onBindWechat: (zbapi_jwt: string) => Promise<void>;
  onClose: () => void;
}

const BindWechatModal: React.FC<BindWechatModalProps> = ({ onBindWechat, onClose }) => {
  const [qrcodeUrl, setQrcodeUrl] = React.useState('');
  const [ticket, setTicket] = React.useState('');
  const [qrcodeLoading, setQrcodeLoading] = React.useState(false);
  const [qrcodeExpired, setQrcodeExpired] = React.useState(false);
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const pollAttemptsRef = React.useRef(0);
  const pollTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const submittedRef = React.useRef(false);
  const pollingTicketRef = React.useRef('');

  const clearPollTimer = React.useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => clearPollTimer();
  }, [clearPollTimer]);

  const fetchQrcode = React.useCallback(async () => {
    setQrcodeLoading(true);
    setError('');
    setQrcodeExpired(false);
    setQrcodeUrl('');
    setTicket('');
    pollAttemptsRef.current = 0;
    submittedRef.current = false;
    clearPollTimer();

    try {
      const response = await authApi.getWechatQrcode();
      const { code_url, ticket: ticketValue } = response.data.data;
      setQrcodeUrl(code_url);
      setTicket(ticketValue);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || '获取微信二维码失败');
    } finally {
      setQrcodeLoading(false);
    }
  }, [clearPollTimer]);

  // 组件挂载时自动获取二维码
  React.useEffect(() => {
    fetchQrcode();
  }, [fetchQrcode]);

  // 轮询扫码状态
  React.useEffect(() => {
    if (!ticket) {
      return () => clearPollTimer();
    }
    if (pollingTicketRef.current === ticket) {
      return () => clearPollTimer();
    }
    pollingTicketRef.current = ticket;
    clearPollTimer();
    pollAttemptsRef.current = 0;
    submittedRef.current = false;

    pollTimerRef.current = setInterval(async () => {
      if (submittedRef.current) return;
      if (pollAttemptsRef.current >= WECHAT_POLL_MAX_ATTEMPTS) {
        clearPollTimer();
        setQrcodeExpired(true);
        pollingTicketRef.current = '';
        return;
      }
      pollAttemptsRef.current += 1;

      try {
        const zbRes = await axios.post(ZBAPI_POLL_URL, { ticket });
        const zbData = zbRes.data;
        const user = zbData?.data?.user;
        const userCode = user?.code;

        if (userCode === 200 && user.access_token && !submittedRef.current) {
          const zbapiJwt = user.access_token;
          submittedRef.current = true;
          clearPollTimer();
          pollingTicketRef.current = '';
          setSubmitting(true);
          setError('');
          try {
            await onBindWechat(zbapiJwt);
            // 绑定成功后，bindState.bind_type 变为 'none'，弹窗条件不成立自动卸载
          } catch (bindErr: any) {
            setError(bindErr?.response?.data?.detail || bindErr?.message || '绑定失败，请重试');
            submittedRef.current = false;
            setQrcodeExpired(true);
          } finally {
            setSubmitting(false);
          }
        } else if (userCode === 400) {
          clearPollTimer();
          setQrcodeExpired(true);
          pollingTicketRef.current = '';
        }
      } catch {
        // 单次失败不中断
      }
    }, WECHAT_POLL_INTERVAL_MS);

    return () => clearPollTimer();
  }, [ticket, onBindWechat, clearPollTimer]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm mx-4 rounded-xl bg-white p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 右上角关闭按钮 */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="关闭"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8.5 10.5c-1.38 0-2.5-.9-2.5-2s1.12-2 2.5-2 2.5.9 2.5 2-1.12 2-2.5 2zm7 0c-1.38 0-2.5-.9-2.5-2s1.12-2 2.5-2 2.5.9 2.5 2-1.12 2-2.5 2zM12 2a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 01-7.56-5.56l2.17 1.15c.34.18.77.06.98-.27l1.2-1.84c.28-.43.13-1.01-.33-1.24C7.48 10.74 7 9.89 7 8.95c0-1.88 1.68-3.41 3.76-3.41 1.29 0 2.44.57 3.17 1.47.35-.1.71-.15 1.07-.15 2.21 0 4 1.56 4 3.48 0 .94-.48 1.79-1.28 2.34-.46.23-.61.81-.33 1.24l1.2 1.84c.21.33.64.45.98.27l2.17-1.15A8 8 0 0112 20z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">绑定微信</h2>
          <p className="mt-1 text-sm text-gray-500">
            用微信扫码绑定账号，下次可直接用微信登录
          </p>
        </div>

        {/* 二维码区域 */}
        <div className="flex flex-col items-center">
          {qrcodeLoading ? (
            <div className="h-56 w-56 animate-pulse rounded-lg bg-gray-100 flex items-center justify-center text-sm text-gray-400">
              加载中...
            </div>
          ) : qrcodeExpired ? (
            <div className="h-56 w-56 rounded-lg border border-dashed border-gray-300 flex flex-col items-center justify-center gap-3">
              <p className="text-sm text-gray-500">二维码已过期</p>
              <button
                type="button"
                onClick={fetchQrcode}
                className="rounded-lg bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600"
              >
                重新获取
              </button>
            </div>
          ) : qrcodeUrl ? (
            <div className="relative">
              <img
                src={qrcodeUrl}
                alt="微信扫码登录二维码"
                className="h-56 w-56 rounded-lg border border-gray-200"
              />
              {submitting && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
                  <span className="text-sm text-white">绑定中...</span>
                </div>
              )}
            </div>
          ) : (
            <div className="h-56 w-56 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-sm text-red-500">
              获取二维码失败
            </div>
          )}

          <p className="mt-3 text-xs text-gray-400">请使用微信扫描二维码</p>

          {error && (
            <div className="mt-3 w-full rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="mt-5 rounded-lg border border-gray-300 px-6 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            稍后绑定
          </button>
        </div>
      </div>
    </div>
  );
};

function LegacyApp() {
  const { user, isLoading, login, logout, wechatLoginByJwt, bindState, bindInfo, bindWechat, setBindState } = useAuth();
  const useLegacyUi = process.env.REACT_APP_USE_LEGACY_UI === 'true';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center text-gray-600">
        正在验证登录状态...
      </div>
    );
  }

  if (!user) {
    return useLegacyUi
      ? <Login onLogin={login} />
      : (
        <VisualLogin
          onLogin={login}
          onWechatLoginByJwt={wechatLoginByJwt}
          wechatLoginEnabled
        />
      );
  }

  draftStorage.setMemberId(user.id);

  const workspace = useLegacyUi
    ? <AuthenticatedApp user={user} onLogout={logout} />
    : <VisualWorkspace user={user} onLogout={logout} />;

  return (
    <>
      {workspace}
      {bindState.need_bind && bindState.bind_type === 'username_password' && (
        <BindInfoModal onBind={bindInfo} onClose={() => setBindState({ need_bind: false, bind_type: 'none' })} />
      )}
      {bindState.need_bind && bindState.bind_type === 'wechat' && (
        <BindWechatModal onBindWechat={bindWechat} onClose={() => setBindState({ need_bind: false, bind_type: 'none' })} />
      )}
    </>
  );
}

const RoutedApp: React.FC = () => {
  const { user, isLoading, login, logout, wechatLoginByJwt, bindState, bindInfo, bindWechat, setBindState } = useAuth();
  const location = useLocation();
  const routeState = location.state as { from?: string } | null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--bid-color-page)] flex items-center justify-center text-slate-600">
        正在验证登录状态...
      </div>
    );
  }

  if (!user) {
    if (location.pathname !== '/login') {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );
    }
    return (
      <VisualLogin
        onLogin={login}
        onWechatLoginByJwt={wechatLoginByJwt}
        wechatLoginEnabled
      />
    );
  }

  draftStorage.setMemberId(user.id);

  if (location.pathname === '/login') {
    const destination = routeState?.from?.startsWith('/')
      ? routeState.from
      : '/projects';
    return <Navigate to={destination} replace />;
  }

  return (
    <>
      <Routes>
        <Route
          path="*"
          element={<VisualWorkspace user={user} onLogout={logout} />}
        />
      </Routes>
      {bindState.need_bind && bindState.bind_type === 'username_password' && (
        <BindInfoModal onBind={bindInfo} onClose={() => setBindState({ need_bind: false, bind_type: 'none' })} />
      )}
      {bindState.need_bind && bindState.bind_type === 'wechat' && (
        <BindWechatModal onBindWechat={bindWechat} onClose={() => setBindState({ need_bind: false, bind_type: 'none' })} />
      )}
    </>
  );
};

function App() {
  if (process.env.REACT_APP_USE_LEGACY_UI === 'true') {
    return <LegacyApp />;
  }

  return (
    <BrowserRouter>
      <RoutedApp />
    </BrowserRouter>
  );
}

export default App;
